import __asset_0 from "@app/assets/img/depersonalized_avatars/1.jpg"
import __asset_1 from "@app/assets/img/depersonalized_avatars/2.jpg"
import __asset_2 from "@app/assets/img/depersonalized_avatars/3.jpg"
import __asset_3 from "@app/assets/img/depersonalized_avatars/4.jpg"
import __asset_4 from "@app/assets/img/depersonalized_avatars/5.jpg"
import __asset_5 from "@app/assets/img/depersonalized_avatars/6.jpg"
import __asset_6 from "@app/assets/img/depersonalized_avatars/7.jpg"
import __asset_7 from "@app/assets/img/depersonalized_avatars/8.jpg"
import isEqual from 'lodash/isEqual'
import Rand from 'rand-seed'

import { createSelector } from '@app/utils/createSelector'
import { isTruthy } from '@app/utils/isTruthy'

import type {
  ApiAnnouncement,
  ApiAnnouncementResponse,
  ApiAvatar,
  ApiDepersonalizedAnnouncement,
  ApiDirectAnnouncement,
  ApiInaccurateLocation,
  ApiLocation,
  ApiRequest,
} from '@app/packages/api/v2/types/entities'
import type { Result } from '@app/packages/Result/Result'
import { intoResult, intoResultCallback, resultError, unwrapResult } from '@app/packages/Result/Result'

import type { StoreUserParent, StoreUserSitter } from '@app/store/types/users'

import { announcementResponsesModelsSelector } from './announcement_responses'
import { announcementsModelsSelector } from './announcements'
import { depersonalizedAnnouncementsModelsSelector } from './depersonalized_announcements'
import { directAnnouncementsModelsSelector } from './direct_announcement'
import { avatarsSelector, inaccurateLocationsSelector, locationsSelector } from './entities'
import { profileUserSelector } from './profile'
import { requestsModelsSelector } from './requests_models'
import { usersModelsSelector } from './users'

export type ApiSitterBundle = {
  user: StoreUserSitter
  avatars: Result<ApiAvatar>[]
}

export type ApiParentBundle = {
  user: StoreUserParent
  avatars: Result<ApiAvatar>[]
}

export type ApiAnnouncementResponseBundle = {
  response: ApiAnnouncementResponse
  sitter: Result<ApiSitterBundle>
}

export type ApiAnnouncementBundle = {
  type: 'announcement'
  announcement: ApiAnnouncement
  location: Result<ApiLocation | null>
  responses: Result<ApiAnnouncementResponseBundle>[]
  parent: Result<ApiParentBundle>
}

export type ApiDepersonalizedAnnouncementBundle = {
  type: 'depersonalized_announcement'
  announcement: ApiDepersonalizedAnnouncement
  location: Result<ApiInaccurateLocation | null>
  depersonalized: DepersonalizedData
}

export type ApiDirectAnnouncementBundle = {
  type: 'direct_announcement'
  direct_announcement: ApiDirectAnnouncement
  announcement: Result<ApiAnnouncement>
  location: Result<ApiLocation | null>
  parent: Result<ApiParentBundle>
  sitter: Result<ApiSitterBundle>
}

export type ApiRequestBundle = {
  type: 'request'
  request: ApiRequest
  announcement: Result<ApiAnnouncement | null>
  location: Result<ApiLocation | null>
  parent: Result<ApiParentBundle>
  sitter: Result<ApiSitterBundle>
}

export class EntityMissingError extends Error {}

export const createAvatarSelector = (id: string) =>
  createResultSelector((state): ApiAvatar => {
    const e = avatarsSelector(state)[id]
    if (!e) throw new EntityMissingError(`Avatar with id ${id} not found`)
    return e
  })

export const createLocationSelector = (id: string) =>
  createResultSelector((state): ApiLocation => {
    const e = locationsSelector(state)[id]
    if (!e) throw new EntityMissingError(`Location with id ${id} not found`)
    return e
  })

export const createInaccurateLocationSelector = (id: string) =>
  createResultSelector((state): ApiInaccurateLocation => {
    const e = inaccurateLocationsSelector(state)[id]
    if (!e) throw new EntityMissingError(`Location with id ${id} not found`)
    return e
  })

export const createAnnouncementResponseSelector = (id: string) =>
  createResultSelector((state): ApiAnnouncementResponse => {
    const e = announcementResponsesModelsSelector(state)[id]
    if (!e) throw new EntityMissingError(`Announcement response with id ${id} not found`)
    return e
  })

export const createAnnouncementSelector = (id: string) =>
  createResultSelector((state): ApiAnnouncement => {
    const e = announcementsModelsSelector(state)[id]
    if (!e) throw new EntityMissingError(`Announcement with id ${id} not found`)
    return e
  })

export const createDepersonalizedAnnouncementSelector = (id: string) =>
  createResultSelector((state): ApiDepersonalizedAnnouncement => {
    const e = depersonalizedAnnouncementsModelsSelector(state)[id]
    if (!e) throw new EntityMissingError(`Announcement with id ${id} not found`)
    return e
  })

export const createDirectAnnouncementSelector = (id: string) =>
  createResultSelector((state): ApiDirectAnnouncement => {
    const e = directAnnouncementsModelsSelector(state)[id]
    if (!e) throw new EntityMissingError(`Direct announcement with id ${id} not found`)
    return e
  })

export const createRequestSelector = (id: string) =>
  createResultSelector((state): ApiRequest => {
    const e = requestsModelsSelector(state)[id]
    if (!e) throw new EntityMissingError(`Request with id ${id} not found`)
    return e
  })

export const createUserBundleSelector = (id: string) =>
  createResultSelector((state): ApiParentBundle | ApiSitterBundle => {
    const users = usersModelsSelector(state)
    const user = users[id]
    if (!user) throw new EntityMissingError(`User with id ${id} not found`)
    if (!user.avatarsComplete) throw new EntityMissingError(`User with id ${id} has incomplete avatars`)
    const accountType = user.account_type
    const avatars = user.avatars.map(a => createAvatarSelector(a.id)(state))

    if (user.account_type === 'parent') {
      return { user, avatars }
    }

    if (user.account_type === 'sitter') {
      return { user, avatars }
    }

    throw new Error(`Unexpected user account type: ${accountType}`)
  })

export const createSitterBundleSelector = (id: string) =>
  createResultSelector(state => {
    const user = unwrapResult(createUserBundleSelector(id)(state))
    if (user.user.account_type !== 'sitter') throw new Error(`Expected user with id ${id} to be a sitter, but got ${user.user.account_type}`)
    return user as ApiSitterBundle
  })

export const createParentBundleSelector = (id: string) =>
  createResultSelector(state => {
    const user = unwrapResult(createUserBundleSelector(id)(state))
    if (user.user.account_type !== 'parent') throw new Error(`Expected user with id ${id} to be a parent, but got ${user.user.account_type}`)
    return user as ApiParentBundle
  })

export const createAnnouncementResponseBundleSelector = (id: string) =>
  createResultSelector((state): ApiAnnouncementResponseBundle => {
    const response = unwrapResult(createAnnouncementResponseSelector(id)(state))
    const sitter = intoResult(() => {
      const id = response.relationships.sitter.data?.id
      if (!id) throw new Error(`Announcement response with id ${id} has no sitter relationship`)
      const sitter = createSitterBundleSelector(id)(state)
      return unwrapResult(sitter)
    })
    return {
      response,
      sitter,
    }
  })

export const createAnnouncementBundleSelector = (announcementId: string) =>
  createResultSelector((state): ApiAnnouncementBundle => {
    const announcement = unwrapResult(createAnnouncementSelector(announcementId)(state))

    const location = intoResult(() => {
      const id = announcement.relationships.location.data?.id
      if (!id) throw new Error(`Announcement with id ${id} has no location relationship`)
      const location = createLocationSelector(id)(state)
      return unwrapResult(location)
    })

    const responses = (() => {
      const data = announcement.relationships.responses.data
      if (!data) return [resultError(`Announcement with id ${announcementId} has no responses relationship`)]
      const ids = data.map(r => r.id)
      return ids.map(
        intoResultCallback(rid => {
          const response = createAnnouncementResponseBundleSelector(rid)(state)
          return unwrapResult(response)
        })
      )
    })()

    const parent = intoResult(() => {
      let id = announcement.relationships.parent.data?.id
      const profileUser = profileUserSelector(state)
      if (!id && profileUser?.account_type === 'parent') {
        id = profileUser.id
      }
      if (!id) throw new Error(`Announcement with id ${announcementId} has no parent relationship`)
      const parent = createParentBundleSelector(id)(state)
      return unwrapResult(parent)
    })

    return {
      type: 'announcement',
      announcement,
      location,
      responses,
      parent,
    }
  })

export const createDepersonalizedAnnouncementBundleSelector = (id: string) =>
  createResultSelector((state): ApiDepersonalizedAnnouncementBundle => {
    const announcement = unwrapResult(createDepersonalizedAnnouncementSelector(id)(state))
    const location = intoResult(() => {
      const id = announcement.relationships.location.data?.id
      if (!id) throw new Error(`Announcement with id ${id} has no location relationship`)
      const location = createInaccurateLocationSelector(id)(state)
      return unwrapResult(location)
    })

    const bundle = {
      type: 'depersonalized_announcement',
      announcement,
      location,
    }

    const depersonalized = getDepersonalizedData(bundle)

    return {
      type: 'depersonalized_announcement',
      announcement,
      location,
      depersonalized,
    }
  })

export const createDirectAnnouncementBundleSelector = (id: string) =>
  createResultSelector((state): ApiDirectAnnouncementBundle => {
    const dannouncement = unwrapResult(createDirectAnnouncementSelector(id)(state))
    const announcement = intoResult(() => {
      const id = dannouncement.relationships.announcement.data?.id
      if (!id) throw new Error(`Direct announcement with id ${id} has no announcement relationship`)
      const announcement = createAnnouncementSelector(id)(state)
      return unwrapResult(announcement)
    })

    const location = intoResult(() => {
      const a = unwrapResult(announcement)
      if (a.relationships.location.data === null) return null
      const id = a.relationships.location.data?.id
      if (!id) throw new Error(`Direct announcement with id ${id} has no location relationship`)
      const location = createLocationSelector(id)(state)
      return unwrapResult(location)
    })

    const parent = intoResult(() => {
      const id = dannouncement.relationships.parent.data?.id
      if (!id) throw new Error(`Direct announcement with id ${id} has no parent relationship`)
      const parent = createParentBundleSelector(id)(state)
      return unwrapResult(parent)
    })

    const sitter = intoResult(() => {
      const id = dannouncement.relationships.sitter.data?.id
      if (!id) throw new Error(`Direct announcement with id ${id} has no sitter relationship`)
      const sitter = createSitterBundleSelector(id)(state)
      return unwrapResult(sitter)
    })

    return {
      type: 'direct_announcement',
      direct_announcement: dannouncement,
      announcement,
      location,
      parent,
      sitter,
    }
  })

export const createRequestBundleSelector = (requestId: string) =>
  createResultSelector((state): ApiRequestBundle => {
    const request = unwrapResult(createRequestSelector(requestId)(state))
    const announcement = intoResult(() => {
      if (request.relationships.announcement.data === null) return null
      const id = request.relationships.announcement.data?.id
      if (!id) throw new Error(`Request with id ${requestId} has no announcement relationship`)
      const announcement = createAnnouncementSelector(id)(state)
      return unwrapResult(announcement)
    })

    const location = intoResult(() => {
      if (request.relationships.location.data === null) return null
      const id = request.relationships.location.data?.id
      if (!id) throw new Error(`Request with id ${requestId} has no location relationship`)
      const location = createLocationSelector(id)(state)
      return unwrapResult(location)
    })

    const parent = intoResult(() => {
      const id = request.relationships.parent.data?.id
      if (!id) throw new Error(`Request with id ${requestId} has no parent relationship`)
      const parent = createParentBundleSelector(id)(state)
      return unwrapResult(parent)
    })

    const sitter = intoResult(() => {
      const id = request.relationships.sitter.data?.id
      if (!id) throw new Error(`Request with id ${requestId} has no sitter relationship`)
      const sitter = createSitterBundleSelector(id)(state)
      return unwrapResult(sitter)
    })

    return {
      type: 'request',
      request,
      announcement,
      location,
      parent,
      sitter,
    }
  })

const stateSelector = (state: unknown) => state
const createResultSelector = <T>(sel: (state: unknown) => T) =>
  createSelector([stateSelector], state => intoResult(() => sel(state)), {
    resultEqual(a, b) {
      return isEqual(a, b)
    },
  })

export type DepersonalizedData = ReturnType<typeof getDepersonalizedData>

const getDepersonalizedData = (bundle: { announcement: ApiDepersonalizedAnnouncement; location: Result<ApiInaccurateLocation | null> }) => {
  const id = bundle.announcement.id
  const location = unwrapResult(bundle.location)
  const rand = new Rand(`${id}${id}`)
  const numberOfSentences = 3 + Math.floor(rand.next() * 5)
  return {
    description: getArrayRandElements(descriptions, `descriptions-${id}`, numberOfSentences).reduce<string>((p, s, index) => {
      if (!index) return s
      return [s, p].join(rand.next() > 0.7 ? '\n\n' : ' ')
    }, ''),
    address: location ? getAddress(new Rand(`location-${location.attributes.rough_latitude}-${location.attributes.rough_longitude}`)) : undefined,
    ...arrayRand(users, rand.next()),
    negative_reviews_count: Math.floor(rand.next() * 10),
    positive_reviews_count: Math.floor(rand.next() * 10),
  }
}

/**
 *
 * @param arr input array
 * @param rand rand number in 0..1 range
 */
const arrayRand = <T>(arr: T[], rand: number) => {
  const index = Math.floor(rand * arr.length)
  return arr[index]
}

const getArrayRandElements = <T>(arr: T[], seed: string, count: number) => {
  const rand = new Rand(seed)
  const tarr = [...arr]
  const result: T[] = []
  for (let i = 0; i < count; i++) {
    const index = Math.floor(rand.next() * arr.length)
    const items = tarr.splice(index, 1)
    result.push(...items)
  }
  return result
}

const descriptions = [
  'Несобственно-прямая речь, ❤️ как бы это ни казалось парадоксальным, начинает композиционный анализ.',
  'Стихотворение интегрирует эпизодический стих.',
  '🚙 Тоника недоступно нивелирует цикл.',
  'Как мы уже знаем, логоэпистема прочно диссонирует верлибр.',
  'Термальный источник, несмотря 🙏🙏🙏 на то, что есть много бунгало для проживания, синхронизирует медиабизнес, отмечает Г.Алмонд.',
  'Лек (L) равен 100 киндаркам, однако понятие модернизации существенно специфицирует плюралистический потребительский рынок, осознав маркетинг как часть производства.',
  'В данной работе мы не будем анализировать все эти аспекты, однако макрель по-прежнему востребована.',
  'Стихотворение теоретически доказывает экспериментальный небольшой парк с дикими животными к юго-западу от Манамы, что получило отражение в трудах Михельса.',
  'Ямб иллюстрирует живописный бальнеоклиматический курорт.',
  'Несмотря на внутренние противоречия, Суэцкий перешеек индуцирует ролевой кризис легитимности.',
  '!!!!Глобализация представляет собой символ.',
  'Стратегическое планирование, как бы это ни казалось парадоксальным, постоянно.',
  'Между тем, щебнистое плато экономит кристаллический фундамент.',
  'Каледонская складчатость усиливает авторитаризм.',
  'Стимулирование коммьюнити последовательно притягивает диссонансный дольник.',
  'Аллегория, на первый взгляд, интегрирует имидж предприятия.',
  'Полярный круг, как бы это ни казалось парадоксальным, традиционно допускает ураган.',
  'Мелькание мыслей представляет собой конструктивный культ личности.',
  'Большая часть территории существенно отражает конструктивный подтекст, осознавая социальную ответственность бизнеса.',
  '😅😅😅 Эпическая медлительность)))), как следует из вышесказанного, означает полифонический роман.',
  'BTL ригиден. 🔥',
  'Анжамбеман, несмотря на то, что есть много бунгало для проживания, слабо притягивает британский протекторат, учитывая результат предыдущих медиа-кампаний.',
  'Палимпсест неизменяем.',
  'Формат события теоретически возможен.',
  'Здесь работали Карл Маркс и Владимир Ленин, но несладкое слоеное тесто, переложенное соленым сыром под названием "сирене", неумеренно означает культурный символ.',
  'Коммуникация методически доказывает коммунальный модернизм.',
  'Диверсификация бизнеса дает брахикаталектический стих, оптимизируя бюджеты.',
  'Согласно теории Э.Тоффлера ("Шок будущего"), многопартийная система пространственно означает конструктивный анапест.',
  'В отличие от произведений поэтов барокко, контент неизбежен.',
  'Модальность высказывания пространственно интегрирует эмпирический цикл, но не рифмами.',
  '🔥 Формат события теоретически возможен. ☺️',
  'Еще Траут показал, что материк аннигилирует плюралистический холодный цинизм.',
  'Дискурс применяет эмпирический традиционный канал.',
  'Молодежная аудитория однообразно иллюстрирует феномен толпы, здесь сохранились остатки построек древнего римского поселения Аквинка - "Аквинкум".',
  'Институциализация, как бы это ни казалось парадоксальным, традиционно редуцирует тактический маркетинг.',
  'Бизнес-стратегия ограничивает не-текст.',
  '❗️❗️❗️❗️❗️',
  '⭐️ Похоже, что самого Бахтина удивила эта всеобщая порабощенность тайной "чужого" слова, тем не менее ритм неоднороден по составу.',
  'Христианско-демократический национализм разнородно отталкивает конструктивный ураган.',
  'Построение бренда, на первый взгляд, поднимает дискурс.',
]

const addresses = [
  'Складническая улица',
  'Проспект Покорителей Детей',
  'Абрахамовская набережная',
  'Вятническая улица',
  'Алабайский бульвар',
  'Меньшиковский проезд',
  'Андреевская улица',
  'Глушкова улица',
  'Бриллиантовый бульвар',
  'Ивановский квартал',
  'Копыльтсовкая улица',
  'Площадь Спиридоновой',
  'Мельниковское Шоссе',
  'Дугиновой проспект',
  'Проскуриной проспект',
  'Дугиновой улица',
  'Улица Балашенко',
  'Азовцево поле',
  'Киреевой проезд',
  'улица Зайнулиной',
  'Щербининой улица',
  'улица Межиной',
  'бульвар Чугуновой',
  'улица Яхиной',
]

const getAddress = (rand: Rand) => {
  const address = {
    street: arrayRand(addresses, rand.next()),
    houseN: range(rand, 1, 100),
    houseSlash: rand.next() > 0.8 ? range(rand, 1, 100) : undefined,
    corp: rand.next() > 0.8 ? range(rand, 1, 10) : undefined,
    build: rand.next() > 0.8 ? range(rand, 1, 10) : undefined,
  }

  return [
    address.street,
    `дом ${address.houseN}${address.houseSlash ? `/${address.houseSlash}` : ''}`,
    address.corp ? `корпус ${address.corp}` : '',
    address.build ? `строение ${address.build}` : '',
  ]
    .filter(isTruthy)
    .join(', ')
}

const range = (rand: Rand, from: number, to: number) => {
  return from + Math.floor(rand.next() * (to - from))
}

const users: {
  name: string
  avatar: string
}[] = [
  {
    name: 'Мария Ф',
    avatar: __asset_0,
  },
  {
    name: 'Евгений В',
    avatar: __asset_1,
  },
  {
    name: 'Злата Л',
    avatar: __asset_2,
  },
  {
    name: 'Саша Г',
    avatar: __asset_3,
  },
  {
    name: 'Алина Б',
    avatar: __asset_4,
  },
  {
    name: 'Наташа К',
    avatar: __asset_5,
  },
  {
    name: 'Оля М',
    avatar: __asset_6,
  },
  {
    name: 'Саша Г',
    avatar: __asset_7,
  },
]
